/**
 * Fuzzy text matching for citation highlighting.
 * Handles OCR errors, punctuation differences, and character variations.
 */

import { distance } from "fastest-levenshtein"

import { normalizeForMatching } from "./normalize-for-matching"

/** Minimum similarity for fuzzy (85%) and weak (70%) */
const FUZZY_THRESHOLD = 0.85
const WEAK_THRESHOLD = 0.7
const MATCH_CACHE_MAX_ENTRIES = 250
const MATCH_CACHE_TTL_MS = 5 * 60 * 1000

export type MatchQuality = "exact" | "fuzzy" | "weak" | "partial" | "none"

export type FuzzyMatchResult = {
  found: boolean
  quality: MatchQuality
  startIndex: number
  endIndex: number
  confidence: number
  isPartial?: boolean
}

type MatchCacheEntry = {
  result: FuzzyMatchResult
  expiresAt: number
}

// Shared in-memory cache for repeated lookup attempts across all viewer types.
const matchLookupCache = new Map<string, MatchCacheEntry>()

function getCachedMatch(cacheKey: string): FuzzyMatchResult | null {
  const cached = matchLookupCache.get(cacheKey)
  if (!cached) return null

  if (cached.expiresAt <= Date.now()) {
    matchLookupCache.delete(cacheKey)
    return null
  }

  // Refresh LRU order on access.
  matchLookupCache.delete(cacheKey)
  matchLookupCache.set(cacheKey, cached)
  return { ...cached.result }
}

function setCachedMatch(cacheKey: string, result: FuzzyMatchResult): void {
  matchLookupCache.set(cacheKey, {
    result: { ...result },
    expiresAt: Date.now() + MATCH_CACHE_TTL_MS,
  })

  while (matchLookupCache.size > MATCH_CACHE_MAX_ENTRIES) {
    const oldestKey = matchLookupCache.keys().next().value
    if (!oldestKey) break
    matchLookupCache.delete(oldestKey)
  }
}

function compactHash(input: string): string {
  let hash = 2166136261
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)
  }
  return (hash >>> 0).toString(36)
}

function buildCacheKey(normalizedContainer: string, normalizedSearch: string): string {
  const containerPrefix = normalizedContainer.slice(0, 24)
  const containerSuffix = normalizedContainer.slice(-24)
  const searchPrefix = normalizedSearch.slice(0, 24)
  const searchSuffix = normalizedSearch.slice(-24)

  return [
    normalizedContainer.length,
    normalizedSearch.length,
    compactHash(normalizedContainer),
    compactHash(normalizedSearch),
    containerPrefix,
    containerSuffix,
    searchPrefix,
    searchSuffix,
  ].join("|")
}

/**
 * Find text in container with multi-level matching strategy:
 * 1. Exact match (after normalization)
 * 2. Fuzzy match with Levenshtein distance on the same normalized text
 *
 * All operations work on normalizeForMatching output to keep positions consistent.
 */
export function findTextWithFuzzyMatching(
  containerText: string,
  searchText: string,
  options: { alreadyNormalized?: boolean } = {}
): FuzzyMatchResult {
  if (!searchText || !searchText.trim()) {
    return { found: false, quality: "none", startIndex: -1, endIndex: -1, confidence: 0 }
  }

  const { alreadyNormalized = false } = options
  const normalizedContainer = alreadyNormalized
    ? containerText
    : normalizeForMatching(containerText)
  const normalizedSearch = alreadyNormalized ? searchText : normalizeForMatching(searchText)
  const cacheKey = buildCacheKey(normalizedContainer, normalizedSearch)
  const cachedResult = getCachedMatch(cacheKey)
  if (cachedResult) {
    return cachedResult
  }

  // Level 1: Exact match (after basic normalization)
  const exactIndex = normalizedContainer.indexOf(normalizedSearch)
  if (exactIndex !== -1) {
    const result: FuzzyMatchResult = {
      found: true,
      quality: "exact",
      startIndex: exactIndex,
      endIndex: exactIndex + normalizedSearch.length,
      confidence: 1.0,
    }
    setCachedMatch(cacheKey, result)
    return result
  }

  // Level 2: Fuzzy match 85%+
  const fuzzyResult = findBestFuzzyMatch(normalizedContainer, normalizedSearch, FUZZY_THRESHOLD)

  if (fuzzyResult) {
    const adjustedEnd = adjustEndBoundary(
      normalizedContainer,
      fuzzyResult.index + normalizedSearch.length
    )
    const result: FuzzyMatchResult = {
      found: true,
      quality: "fuzzy",
      startIndex: fuzzyResult.index,
      endIndex: adjustedEnd,
      confidence: fuzzyResult.confidence,
    }
    setCachedMatch(cacheKey, result)
    return result
  }

  // Level 3: Partial match - try shorter prefixes (80%, 60%, 40%, min 30 chars)
  const partialResult = findPartialMatch(normalizedContainer, normalizedSearch)
  if (partialResult) {
    const result: FuzzyMatchResult = {
      found: true,
      quality: "partial",
      startIndex: partialResult.index,
      endIndex: partialResult.endIndex,
      confidence: partialResult.confidence,
      isPartial: true,
    }
    setCachedMatch(cacheKey, result)
    return result
  }

  // Level 4: Weak match 70-84%
  const weakResult = findBestFuzzyMatch(normalizedContainer, normalizedSearch, WEAK_THRESHOLD)

  if (weakResult && weakResult.confidence < FUZZY_THRESHOLD) {
    const adjustedEnd = adjustEndBoundary(
      normalizedContainer,
      weakResult.index + normalizedSearch.length
    )
    const result: FuzzyMatchResult = {
      found: true,
      quality: "weak",
      startIndex: weakResult.index,
      endIndex: adjustedEnd,
      confidence: weakResult.confidence,
    }
    setCachedMatch(cacheKey, result)
    return result
  }

  const result: FuzzyMatchResult = {
    found: false,
    quality: "none",
    startIndex: -1,
    endIndex: -1,
    confidence: 0,
  }
  setCachedMatch(cacheKey, result)
  return result
}

type FuzzyMatchInternal = { index: number; confidence: number }

function findBestFuzzyMatch(
  container: string,
  search: string,
  minThreshold: number
): FuzzyMatchInternal | null {
  const needleLen = search.length
  let bestMatch: FuzzyMatchInternal | null = null

  for (let i = 0; i <= container.length - needleLen; i++) {
    const window = container.substring(i, i + needleLen)
    const maxLen = Math.max(window.length, search.length)
    if (maxLen === 0) continue

    const dist = distance(window, search)
    const similarity = 1 - dist / maxLen

    if (similarity >= minThreshold) {
      const charBefore = i > 0 ? container[i - 1] : " "
      const firstChar = container[i]
      const isSpaceBoundary = /\s/.test(charBefore)
      const isMissingSpaceBoundary = /[a-zåäö]/.test(charBefore) && /[A-ZÅÄÖ]/.test(firstChar)
      const isWordBoundary = isSpaceBoundary || isMissingSpaceBoundary
      const isMidWord = /[a-zåäö]/.test(charBefore) && /[a-zåäö]/.test(firstChar)

      let adjustedSimilarity = similarity
      if (isMidWord) {
        adjustedSimilarity -= 0.1
      } else if (isWordBoundary) {
        adjustedSimilarity += 0.05
      }

      if (!bestMatch || adjustedSimilarity > bestMatch.confidence) {
        bestMatch = { index: i, confidence: similarity }
      }
    }
  }
  return bestMatch
}

function adjustEndBoundary(container: string, end: number): number {
  const searchArea = container.substring(Math.max(0, end - 10), end + 20)
  const periodMatch = /\.\s*[A-ZÅÄÖ]/.exec(searchArea)
  if (periodMatch && periodMatch.index < 15) {
    return end - 10 + periodMatch.index + 1
  }
  return end
}

function findPartialMatch(
  container: string,
  search: string
): (FuzzyMatchInternal & { endIndex: number }) | null {
  const len = search.length
  const minLen = 30
  const ratios = [0.8, 0.6, 0.4]

  for (const ratio of ratios) {
    const partialLen = Math.max(minLen, Math.floor(len * ratio))
    if (partialLen > len) continue

    const partialSearch = search.slice(0, partialLen)
    const match = findBestFuzzyMatch(container, partialSearch, FUZZY_THRESHOLD)
    if (match) {
      return {
        ...match,
        endIndex: match.index + partialLen,
      }
    }
  }
  return null
}
